import React from 'react';
import { useTranslation } from 'react-i18next';
import { HiCalendar } from 'react-icons/hi';
import { FaCheckCircle, FaCoins } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import Button from './Button';
import AttendanceComponent from './AttendanceComponent';
import EventCard from './EventCard';
import { EventParticipationAnswer, EventParticipant } from '../../types/event';

interface EventsTableProps {
  events: any[];
  onRowClick?: (id: number) => void;
  onEdit?: (id: number) => void;
  canEdit?: boolean;
  onRsvpChange?: (eventId: number, answer: EventParticipationAnswer) => void;
  getCurrentUserParticipant?: (event: any) => EventParticipant | undefined;
  onBuyTicket?: (id: number) => void;
}

export default function EventsTable({
  events,
  onRowClick,
  onEdit,
  canEdit,
  onRsvpChange,
  getCurrentUserParticipant,
  onBuyTicket,
}: EventsTableProps) {
  const { t } = useTranslation();

  const formatDate = (dateString: Date | string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: Date | string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateTime = (dateString: Date | string) => `${formatDate(dateString)} at ${formatTime(dateString)}`;

  return (
    <>
      {/* Desktop / Table (md and up) */}
      <div className="hidden overflow-hidden rounded-lg md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-details/10 bg-secondary">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-primary">
                  {t('page.dashboard.event.title')}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-primary">
                  {t('page.dashboard.event.startTime')}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-primary">
                  {t('page.dashboard.event.location')}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-primary">
                  {t('page.dashboard.event.yourRsvp')}
                </th>
                {canEdit && (
                  <th className="px-6 py-4 text-left text-sm font-semibold text-primary">{t('general.actions')}</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-details/10">
              {events.map((event) => {
                const currentUserParticipant = getCurrentUserParticipant ? getCurrentUserParticipant(event) : undefined;

                return (
                  <tr
                    key={event.id}
                    onClick={() => onRowClick && onRowClick(event.id)}
                    className={`${onRowClick ? 'hover:bg-secondary-100 cursor-pointer' : 'transition-colors'}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        {event.picture ? (
                          <img
                            src={event.picture}
                            alt={event.title}
                            className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                            <HiCalendar className="h-6 w-6 text-primary" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-primary">{event.title}</p>
                          {event.description && (
                            <p className="line-clamp-1 text-sm text-details/70">{event.description}</p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="whitespace-nowrap px-6 py-4 text-sm text-details/80">
                      {formatDateTime(event.startTime)}
                    </td>

                    <td className="px-6 py-4 text-sm text-details/80">{event.location || '-'}</td>

                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      {event.priceOfTicket ? (
                        currentUserParticipant?.answer === EventParticipationAnswer.YES ? (
                          <span className="inline-flex items-center gap-2 rounded-full bg-success/20 px-2 py-0.5 text-xs font-medium text-success">
                            <FaCheckCircle className="h-4 w-4" />
                            {t('page.payment.status.PAID')}
                          </span>
                        ) : (
                          <Button
                            variant="primary"
                            className="inline-flex items-center gap-2"
                            onClick={() => onBuyTicket && onBuyTicket(event.id)}
                            aria-label={t('page.events.invite.buyTicket')}>
                            <FaCoins className="h-4 w-4" />
                            {t('page.events.invite.buyTicket')}
                          </Button>
                        )
                      ) : currentUserParticipant?.answer ? (
                        <div className="flex origin-left scale-75 justify-start">
                          <AttendanceComponent
                            selectedAnswer={currentUserParticipant.answer}
                            setSelectedAnswer={(answer: EventParticipationAnswer) =>
                              onRsvpChange && onRsvpChange(event.id, answer)
                            }
                            disableYes={false}
                          />
                        </div>
                      ) : (
                        <span className="text-sm text-details/70">-</span>
                      )}
                    </td>

                    {canEdit && (
                      <td className="px-6 py-4 text-sm text-details/80" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => {
                            if (onEdit) onEdit(event.id);
                          }}
                          aria-label={t('general.edit') || 'Edit'}
                          className="rounded bg-secondary-50 p-2 text-primary">
                          <FiEdit2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile list (sm and down) */}
      <div className="flex flex-col gap-3 md:hidden">
        {events.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            onEdit={
              canEdit
                ? (id) => {
                    if (onEdit) onEdit(id);
                  }
                : undefined
            }
          />
        ))}
      </div>
    </>
  );
}
